import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Users, FileText, MessageSquare, BarChart3, CheckCircle, Settings, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Download, Eye, X, Scale, Activity, Dumbbell, Droplets, Heart, Brain, Flame, Bone, KeyRound, ArrowLeft, Star } from 'lucide-react';
import { AdminReviews } from '@/components/AdminReviews';
import { SetPasswordDialog } from '@/components/SetPasswordDialog';
import axios from 'axios';
import { toast } from 'sonner';
import { useLang } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { id: 'overview', icon: BarChart3 },
  { id: 'assessments', icon: FileText },
  { id: 'clients', icon: Users },
  { id: 'contacts', icon: MessageSquare },
  { id: 'reviews', icon: Star },
  { id: 'builder', icon: Settings },
];

const QUESTION_TYPES = ['text', 'number', 'textarea', 'radio', 'checkbox', 'date', 'scale'];

const SNAPSHOT = [
  { key: 'weight', label: 'bia.weight', unit: 'kg', color: '#D4AF37' },
  { key: 'body_fat_pct', label: 'bia.body_fat_pct', unit: '%', color: '#E2B4BD' },
  { key: 'muscle_mass', label: 'bia.muscle_short', unit: 'kg', color: '#7AE0A0' },
  { key: 'total_body_water', label: 'bia.water_short', unit: 'L', color: '#5BA8D4' },
  { key: 'bmi', label: 'bia.bmi_short', unit: '', color: '#D4AF37' },
  { key: 'visceral_fat', label: 'bia.visceral_short', unit: '', color: '#D45A5A' },
  { key: 'metabolic_age', label: 'bia.metabolic_short', unit: 'yrs', color: '#B07AE0' },
];

const FULL_BIA = [
  ['weight', 'kg'], ['body_fat_pct', '%'], ['body_fat_mass', 'kg'], ['muscle_mass', 'kg'], ['fat_free_mass', 'kg'],
  ['total_body_water', 'L'], ['body_water_pct', '%'], ['bone_mass', 'kg'], ['bmi', 'kg/m²'], ['bmr', 'kcal'],
  ['visceral_fat', 'level'], ['metabolic_age', 'yrs'], ['protein_pct', '%'], ['phase_angle', '°'],
];

const BIA_FORM_GROUPS = [
  { title: 'bia.sec_core', cols: 3, fields: ['weight', 'body_fat_pct', 'body_fat_mass'] },
  { title: 'admin.sec_muscle', cols: 2, fields: ['muscle_mass', 'fat_free_mass'] },
  { title: 'bia.sec_water', cols: 2, fields: ['total_body_water', 'body_water_pct'] },
  { title: 'admin.sec_bone_met', cols: 3, fields: ['bone_mass', 'bmi', 'bmr', 'visceral_fat', 'metabolic_age'] },
  { title: 'bia.sec_additional', cols: 2, fields: ['protein_pct', 'phase_angle'] },
];
const UNIT_OF = Object.fromEntries(FULL_BIA);

const AdminPage = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [passwordClient, setPasswordClient] = useState(null);
  const [assessmentResponses, setAssessmentResponses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [assessmentConfig, setAssessmentConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  // Client BIA state
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientProgress, setClientProgress] = useState([]);
  const [showBiaForm, setShowBiaForm] = useState(false);
  const [biaForm, setBiaForm] = useState({});
  const [biaSubmitting, setBiaSubmitting] = useState(false);
  const { t, pick, lang, fmtDate, fmtDateTime } = useLang();

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) navigate('/login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (token && user?.role === 'admin') {
      const h = { Authorization: `Bearer ${token}` };
      axios.get(`${API}/admin/stats`, { headers: h }).then(r => setStats(r.data)).catch(() => {});
      axios.get(`${API}/admin/clients`, { headers: h }).then(r => setClients(r.data)).catch(() => {});
      axios.get(`${API}/admin/assessment/responses`, { headers: h }).then(r => setAssessmentResponses(r.data)).catch(() => {});
      axios.get(`${API}/admin/contacts`, { headers: h }).then(r => setContacts(r.data)).catch(() => {});
      axios.get(`${API}/admin/assessment/config`, { headers: h }).then(r => setAssessmentConfig(r.data)).catch(() => {});
    }
  }, [token, user]);

  const resolveContact = async (id) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}/status`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(contacts.map(c => c.id === id ? { ...c, status: 'resolved' } : c));
      toast.success(t('admin.resolved_toast'));
    } catch { toast.error(t('admin.update_fail')); }
  };

  const signAssessment = async (id) => {
    try {
      await axios.put(`${API}/admin/assessment/${id}/sign`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAssessmentResponses(assessmentResponses.map(r => r.id === id ? { ...r, coach_signed: true, coach_signed_at: new Date().toISOString(), coach_signer_name: user.name } : r));
      toast.success(t('admin.signed_toast'));
    } catch { toast.error(t('admin.sign_fail')); }
  };

  const selectClient = async (client) => {
    setSelectedClient(client);
    try {
      const res = await axios.get(`${API}/admin/clients/${client.id}/progress`, { headers: { Authorization: `Bearer ${token}` } });
      setClientProgress(res.data.progress || []);
    } catch { setClientProgress([]); }
  };

  const submitBiaForClient = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    const payload = {};
    const biaKeys = ['weight','body_fat_pct','body_fat_mass','muscle_mass','fat_free_mass','total_body_water','body_water_pct','bone_mass','bmi','bmr','visceral_fat','metabolic_age','protein_pct','phase_angle'];
    biaKeys.forEach(k => { if (biaForm[k] !== undefined && biaForm[k] !== '') payload[k] = parseFloat(biaForm[k]); });
    if (biaForm.notes) payload.notes = biaForm.notes;
    if (Object.keys(payload).filter(k => k !== 'notes').length === 0) {
      toast.error(t('bia.need_one'));
      return;
    }
    setBiaSubmitting(true);
    try {
      await axios.post(`${API}/admin/clients/${selectedClient.id}/progress`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('admin.bia_logged_for', { name: selectedClient.name }));
      setBiaForm({});
      setShowBiaForm(false);
      selectClient(selectedClient);
    } catch { toast.error(t('admin.bia_fail')); }
    setBiaSubmitting(false);
  };

  const deleteClient = async (client) => {
    if (!window.confirm(t('admin.confirm_delete_client', { name: client.name }))) return;
    try {
      await axios.delete(`${API}/admin/clients/${client.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(prev => prev.filter(c => c.id !== client.id));
      toast.success(t('admin.client_deleted'));
    } catch { toast.error(t('admin.update_fail')); }
  };

  const downloadClientReport = (clientId) => {
    window.open(`${API}/admin/clients/${clientId}/report/pdf?token=${token}&lang=${lang}`, '_blank');
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/assessment/config`, assessmentConfig, { headers: { Authorization: `Bearer ${token}` } });
      setAssessmentConfig(res.data);
      toast.success(t('admin.config_saved'));
    } catch { toast.error(t('admin.save_fail')); }
    setSaving(false);
  };

  if (authLoading || !stats) {
    return <div className="pt-20 min-h-screen flex items-center justify-center"><p className="text-[#A3A19C]">{t('common.loading')}</p></div>;
  }

  return (
    <div className="pt-20 min-h-screen" data-testid="admin-page">
      <section className="py-8 lg:py-12 bg-[#121215] border-b border-[rgba(255,255,255,0.06)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="overline mb-2">{t('admin.overline')}</p>
          <h1 className="text-3xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">
            {t('admin.h_1')} <span className="gold-gradient-text">{t('admin.h_2')}</span>
          </h1>
        </div>
      </section>

      <div className="border-b border-[rgba(255,255,255,0.06)] sticky top-20 z-40 bg-[#08080A]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex gap-1 overflow-x-auto" data-testid="admin-tabs">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`admin-tab-${tab.id}`}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition-colors duration-300 whitespace-nowrap ${
                  activeTab === tab.id ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-transparent text-[#A3A19C] hover:text-[#F5F2EB]'
                }`}>
                <tab.icon className="w-4 h-4" strokeWidth={1.5} /> {t(`admin.tab_${tab.id}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Overview */}
        {activeTab === 'overview' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-overview">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              {[
                { label: t('admin.total_clients'), value: stats.total_clients, icon: Users, color: '#D4AF37' },
                { label: t('admin.assessments'), value: stats.total_responses, icon: FileText, color: '#E2B4BD' },
                { label: t('admin.new_messages'), value: stats.new_contacts, icon: MessageSquare, color: '#D4AF37' },
                { label: t('admin.programs'), value: stats.total_programs, icon: BarChart3, color: '#E2B4BD' },
              ].map((stat, i) => (
                <div key={stat.label} className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-6" data-testid={`admin-stat-${i}`}>
                  <stat.icon className="w-5 h-5 mb-3" style={{ color: stat.color }} strokeWidth={1.5} />
                  <p className="text-3xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{stat.value}</p>
                  <p className="text-xs text-[#A3A19C] mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-6">
                <h3 className="text-lg font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-4">{t('admin.recent_assessments')}</h3>
                <div className="space-y-3">
                  {assessmentResponses.slice(0, 5).map(r => (
                    <div key={r.id} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.05)] last:border-0">
                      <div>
                        <p className="text-sm text-[#F5F2EB]">{r.answers?.full_name || t('common.anonymous')}</p>
                        <p className="text-xs text-[#A3A19C] ltr-iso">{r.answers?.phone || '--'}</p>
                      </div>
                      <p className="text-xs text-[#A3A19C]">{fmtDate(r.submitted_at)}</p>
                    </div>
                  ))}
                  {assessmentResponses.length === 0 && <p className="text-sm text-[#A3A19C]">{t('admin.no_assessments')}</p>}
                </div>
              </div>
              <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-6">
                <h3 className="text-lg font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-4">{t('admin.recent_messages')}</h3>
                <div className="space-y-3">
                  {contacts.slice(0, 5).map(c => (
                    <div key={c.id} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.05)] last:border-0">
                      <div>
                        <p className="text-sm text-[#F5F2EB]">{c.name}</p>
                        <p className="text-xs text-[#A3A19C]">{c.subject}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'new' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-green-500/10 text-green-400'}`}>{t(`admin.status_${c.status}`)}</span>
                    </div>
                  ))}
                  {contacts.length === 0 && <p className="text-sm text-[#A3A19C]">{t('admin.no_messages')}</p>}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Assessments */}
        {activeTab === 'assessments' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-assessments">
            <div className="space-y-4">
              {assessmentResponses.map(r => (
                <AssessmentResponseCard key={r.id} response={r} config={assessmentConfig} onSign={signAssessment} token={token} />
              ))}
              {assessmentResponses.length === 0 && <p className="text-sm text-[#A3A19C] text-center py-12">{t('admin.no_responses')}</p>}
            </div>
          </motion.div>
        )}

        {/* Clients with BIA Management */}
        {activeTab === 'clients' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-clients">
            {!selectedClient ? (
              /* Client List */
              <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[rgba(255,255,255,0.08)]">
                        <th className="text-start px-6 py-4 text-xs text-[#A3A19C] font-semibold uppercase tracking-wider">{t('admin.col_name')}</th>
                        <th className="text-start px-6 py-4 text-xs text-[#A3A19C] font-semibold uppercase tracking-wider">{t('admin.col_email')}</th>
                        <th className="text-start px-6 py-4 text-xs text-[#A3A19C] font-semibold uppercase tracking-wider">{t('admin.col_phone')}</th>
                        <th className="text-start px-6 py-4 text-xs text-[#A3A19C] font-semibold uppercase tracking-wider">{t('admin.col_joined')}</th>
                        <th className="text-end px-6 py-4 text-xs text-[#A3A19C] font-semibold uppercase tracking-wider">{t('admin.col_actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map(client => (
                        <tr key={client.id} className="border-b border-[rgba(255,255,255,0.05)] hover:bg-[#1A1A1E] transition-colors duration-200">
                          <td className="px-6 py-4 text-sm text-[#F5F2EB]">{client.name}</td>
                          <td className="px-6 py-4 text-sm text-[#A3A19C]">{client.email}</td>
                          <td className="px-6 py-4 text-sm text-[#A3A19C]"><span className="ltr-iso">{client.phone || '--'}</span></td>
                          <td className="px-6 py-4 text-sm text-[#A3A19C]">{fmtDate(client.created_at)}</td>
                          <td className="px-6 py-4 text-end">
                            <div className="flex items-center justify-end gap-2">
                              <button onClick={() => selectClient(client)} className="text-xs text-[#D4AF37] hover:text-[#E5C07B] transition-colors duration-300 flex items-center gap-1" data-testid={`view-client-${client.id}`}>
                                <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('admin.bia_profile')}
                              </button>
                              <button onClick={() => downloadClientReport(client.id)} className="text-xs text-[#A3A19C] hover:text-[#F5F2EB] transition-colors duration-300 flex items-center gap-1" data-testid={`download-client-report-${client.id}`}>
                                <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('admin.report')}
                              </button>
                              <button onClick={() => setPasswordClient(client)} className="text-xs text-[#A3A19C] hover:text-[#F5F2EB] transition-colors duration-300 flex items-center gap-1" data-testid={`set-password-${client.id}`}>
                                <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('admin.password')}
                              </button>
                              <button onClick={() => deleteClient(client)} className="text-xs text-red-400/80 hover:text-red-400 transition-colors duration-300 flex items-center gap-1" data-testid={`delete-client-${client.id}`}>
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('rev.delete')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {clients.length === 0 && <p className="text-sm text-[#A3A19C] p-6 text-center">{t('admin.no_clients')}</p>}
                {passwordClient && <SetPasswordDialog client={passwordClient} token={token} onClose={() => setPasswordClient(null)} />}
              </div>
            ) : (
              /* Client BIA Profile */
              <div data-testid="client-bia-profile">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                  <div>
                    <button onClick={() => { setSelectedClient(null); setClientProgress([]); }} className="text-xs text-[#A3A19C] hover:text-[#F5F2EB] mb-2 flex items-center gap-1" data-testid="back-to-clients">
                      <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('admin.back_clients')}
                    </button>
                    <h2 className="text-2xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{selectedClient.name}</h2>
                    <p className="text-sm text-[#A3A19C]">{selectedClient.email} {selectedClient.phone && <>· <span className="ltr-iso">{selectedClient.phone}</span></>}</p>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setShowBiaForm(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="admin-add-bia-btn">
                      <Plus className="w-4 h-4" strokeWidth={1.5} /> {t('dash.log')}
                    </button>
                    <button onClick={() => downloadClientReport(selectedClient.id)} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="admin-download-report">
                      <Download className="w-4 h-4" strokeWidth={1.5} /> {t('common.download_report')}
                    </button>
                  </div>
                </div>

                {/* Latest BIA Snapshot */}
                {clientProgress.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
                    {SNAPSHOT.map(item => {
                      const val = clientProgress[0]?.[item.key];
                      return (
                        <div key={item.key} className="bg-[#08080A] border border-[rgba(255,255,255,0.06)] rounded-lg p-4">
                          <p className="text-[10px] text-[#A3A19C] uppercase tracking-wider mb-1">{t(item.label)}</p>
                          <p className="text-xl font-['Cormorant_Garamond'] font-bold" style={{ color: val != null ? item.color : '#1A1A1E' }}>{val ?? '--'}</p>
                          <p className="text-[10px] text-[#A3A19C]">{t(`unit.${item.unit}`)}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Full BIA Breakdown */}
                {clientProgress.length > 0 && (
                  <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-4">{t('admin.latest_full')}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                      {FULL_BIA.map(([key, unit]) => ({ key, unit, label: `bia.${key}` })).map(item => {
                        const val = clientProgress[0]?.[item.key];
                        return val != null ? (
                          <div key={item.key} className="bg-[#08080A] rounded-lg px-4 py-3">
                            <p className="text-[10px] text-[#A3A19C] uppercase tracking-wider">{t(item.label)}</p>
                            <p className="text-base font-semibold text-[#F5F2EB] mt-1">{val} <span className="text-[10px] text-[#A3A19C]">{t(`unit.${item.unit}`)}</span></p>
                          </div>
                        ) : null;
                      })}
                    </div>
                    <p className="text-[10px] text-[#8C6C73] mt-4">{fmtDateTime(clientProgress[0]?.created_at)} {clientProgress[0]?.entered_by_name && `· ${t('admin.entered_by', { name: clientProgress[0].entered_by_name })}`}</p>
                  </div>
                )}

                {/* BIA History */}
                <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b border-[rgba(255,255,255,0.06)]">
                    <h3 className="text-lg font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{t('bia.history')}</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[rgba(255,255,255,0.06)]">
                          <th className="text-start px-4 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.date')}</th>
                          <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.weight')}</th>
                          <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.fat_pct_short')}</th>
                          <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.muscle_short')}</th>
                          <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.water_short')}</th>
                          <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.bmi_short')}</th>
                          <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.visceral_short')}</th>
                          <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.metabolic_short')}</th>
                          <th className="text-start px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.by')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientProgress.map(entry => (
                          <tr key={entry.id} className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[#1A1A1E]">
                            <td className="px-4 py-3 text-xs text-[#A3A19C]">{fmtDate(entry.created_at)}</td>
                            <td className="text-end px-3 py-3 text-xs text-[#F5F2EB]">{entry.weight ?? '-'}</td>
                            <td className="text-end px-3 py-3 text-xs text-[#E2B4BD]">{entry.body_fat_pct ?? '-'}</td>
                            <td className="text-end px-3 py-3 text-xs text-[#7AE0A0]">{entry.muscle_mass ?? '-'}</td>
                            <td className="text-end px-3 py-3 text-xs text-[#5BA8D4]">{entry.total_body_water ?? '-'}</td>
                            <td className="text-end px-3 py-3 text-xs text-[#D4AF37]">{entry.bmi ?? '-'}</td>
                            <td className="text-end px-3 py-3 text-xs text-[#D45A5A]">{entry.visceral_fat ?? '-'}</td>
                            <td className="text-end px-3 py-3 text-xs text-[#B07AE0]">{entry.metabolic_age ?? '-'}</td>
                            <td className="px-3 py-3 text-xs text-[#8C6C73]">{entry.entered_by_name || t('bia.client')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {clientProgress.length === 0 && <p className="text-sm text-[#A3A19C] p-6 text-center">{t('admin.no_client_bia')}</p>}
                </div>

                {/* BIA Entry Modal */}
                {showBiaForm && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#08080A]/80 backdrop-blur-sm px-4" onClick={() => setShowBiaForm(false)}>
                    <div className="bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="admin-bia-modal">
                      <div className="sticky top-0 bg-[#121215] border-b border-[rgba(255,255,255,0.06)] px-8 py-5 flex items-center justify-between z-10">
                        <div>
                          <h3 className="text-xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{t('admin.log_for', { name: selectedClient.name })}</h3>
                          <p className="text-xs text-[#A3A19C]">{t('admin.log_for_sub')}</p>
                        </div>
                        <button onClick={() => setShowBiaForm(false)} className="text-[#A3A19C] hover:text-[#F5F2EB]"><X className="w-5 h-5" strokeWidth={1.5} /></button>
                      </div>
                      <form onSubmit={submitBiaForClient} className="px-8 py-6 space-y-5" data-testid="admin-bia-form">
                        {BIA_FORM_GROUPS.map(g => (
                          <div key={g.title}>
                            <p className="text-sm font-semibold text-[#D4AF37] mb-3">{t(g.title)}</p>
                            <div className={`grid gap-3 ${g.cols === 3 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2'}`}>
                              {g.fields.map(f => (
                                <AdminBIAInput key={f} label={`${t(`bia.${f}`)} (${t(`unit.${UNIT_OF[f]}`)})`} field={f} form={biaForm} setForm={setBiaForm} />
                              ))}
                            </div>
                          </div>
                        ))}
                        <div>
                          <label className="block text-xs text-[#A3A19C] mb-1">{t('common.notes')}</label>
                          <input type="text" value={biaForm.notes || ''} onChange={e => setBiaForm({ ...biaForm, notes: e.target.value })}
                            className="w-full bg-[#08080A] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37]" placeholder={t('common.notes')} />
                        </div>
                        <div className="flex gap-3 pt-2">
                          <button type="submit" disabled={biaSubmitting} className="btn-primary text-sm" data-testid="admin-bia-submit">
                            {biaSubmitting ? t('common.saving') : t('bia.save')}
                          </button>
                          <button type="button" onClick={() => setShowBiaForm(false)} className="btn-secondary text-sm">{t('common.cancel')}</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* Contacts */}
        {activeTab === 'contacts' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-contacts">
            <div className="space-y-4">
              {contacts.map(c => (
                <div key={c.id} className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-[#F5F2EB]">{c.name}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${c.status === 'new' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-green-500/10 text-green-400'}`}>{t(`admin.status_${c.status}`)}</span>
                      </div>
                      <p className="text-xs text-[#A3A19C]">{c.email} {c.phone && <>· <span className="ltr-iso">{c.phone}</span></>} · {fmtDateTime(c.created_at)}</p>
                    </div>
                    {c.status === 'new' && (
                      <button onClick={() => resolveContact(c.id)} className="text-xs text-[#A3A19C] hover:text-green-400 transition-colors duration-300 flex items-center gap-1" data-testid={`resolve-${c.id}`}>
                        <CheckCircle className="w-4 h-4" strokeWidth={1.5} /> {t('admin.resolve')}
                      </button>
                    )}
                  </div>
                  <p className="text-xs font-semibold text-[#E2B4BD] mb-1">{c.subject}</p>
                  <p className="text-sm text-[#A3A19C]">{c.message}</p>
                </div>
              ))}
              {contacts.length === 0 && <p className="text-sm text-[#A3A19C] text-center py-12">{t('admin.no_messages')}</p>}
            </div>
          </motion.div>
        )}

        {activeTab === 'reviews' && <AdminReviews token={token} />}

        {/* Form Builder */}
        {activeTab === 'builder' && assessmentConfig && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-builder">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{t('admin.builder_title')}</h2>
                <p className="text-sm text-[#A3A19C]">{t('admin.builder_sub')}</p>
              </div>
              <button onClick={saveConfig} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="save-config-btn">
                {saving ? t('common.saving') : t('admin.save_changes')}
              </button>
            </div>

            {/* Meta */}
            <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-6 mb-6">
              <h3 className="text-sm font-semibold text-[#D4AF37] mb-4 uppercase tracking-wider">{t('admin.general')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField label={t('admin.title_en')} value={assessmentConfig.title || ''} onChange={v => setAssessmentConfig({ ...assessmentConfig, title: v })} testId="config-title" />
                <InputField label={t('admin.title_ar')} value={assessmentConfig.title_ar || ''} onChange={v => setAssessmentConfig({ ...assessmentConfig, title_ar: v })} testId="config-title-ar" />
                <InputField label={t('admin.desc_en')} value={assessmentConfig.description || ''} onChange={v => setAssessmentConfig({ ...assessmentConfig, description: v })} testId="config-desc" multiline />
                <InputField label={t('admin.desc_ar')} value={assessmentConfig.description_ar || ''} onChange={v => setAssessmentConfig({ ...assessmentConfig, description_ar: v })} testId="config-desc-ar" multiline />
              </div>
            </div>

            {/* Acknowledgment */}
            <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-6 mb-6">
              <h3 className="text-sm font-semibold text-[#D4AF37] mb-4 uppercase tracking-wider">{t('admin.ack_text')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField label={t('admin.ack_en')} value={assessmentConfig.acknowledgment_text || ''} onChange={v => setAssessmentConfig({ ...assessmentConfig, acknowledgment_text: v })} testId="config-ack" multiline />
                <InputField label={t('admin.ack_ar')} value={assessmentConfig.acknowledgment_text_ar || ''} onChange={v => setAssessmentConfig({ ...assessmentConfig, acknowledgment_text_ar: v })} testId="config-ack-ar" multiline />
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-4">
              {(assessmentConfig.sections || []).map((section, sIdx) => (
                <SectionEditor
                  key={section.id || sIdx}
                  section={section}
                  sectionIndex={sIdx}
                  allQuestions={getAllQuestions(assessmentConfig)}
                  onUpdate={(updated) => {
                    const sections = [...assessmentConfig.sections];
                    sections[sIdx] = updated;
                    setAssessmentConfig({ ...assessmentConfig, sections });
                  }}
                  onDelete={() => {
                    const sections = assessmentConfig.sections.filter((_, i) => i !== sIdx);
                    setAssessmentConfig({ ...assessmentConfig, sections });
                  }}
                />
              ))}
            </div>

            <button
              onClick={() => {
                const sections = [...(assessmentConfig.sections || [])];
                sections.push({ id: '', title: 'New Section', title_ar: 'قسم جديد', order: sections.length, questions: [] });
                setAssessmentConfig({ ...assessmentConfig, sections });
              }}
              className="mt-4 w-full py-3 border border-dashed border-[rgba(255,255,255,0.15)] rounded-lg text-sm text-[#A3A19C] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
              data-testid="add-section-btn"
            >
              <Plus className="w-4 h-4 inline me-1" strokeWidth={1.5} /> {t('admin.add_section')}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Helper: get all questions flat
const getAllQuestions = (config) => {
  if (!config?.sections) return [];
  return config.sections.flatMap(s => s.questions || []);
};

// Input field component
const InputField = ({ label, value, onChange, testId, multiline }) => (
  <div>
    <label className="block text-xs text-[#A3A19C] mb-1.5">{label}</label>
    {multiline ? (
      <textarea rows={3} value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-[#08080A] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 resize-none" />
    ) : (
      <input type="text" value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-[#08080A] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors duration-300" />
    )}
  </div>
);

// Section Editor
const SectionEditor = ({ section, sectionIndex, allQuestions, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const { t, pick } = useLang();

  const addQuestion = () => {
    const questions = [...(section.questions || [])];
    questions.push({
      id: '', text: 'New Question', text_ar: 'سؤال جديد', type: 'text',
      required: true, order: questions.length, options: null,
      conditional_parent_id: null, conditional_trigger_value: null,
    });
    onUpdate({ ...section, questions });
  };

  const updateQuestion = (qIdx, updated) => {
    const questions = [...section.questions];
    questions[qIdx] = updated;
    onUpdate({ ...section, questions });
  };

  const deleteQuestion = (qIdx) => {
    const questions = section.questions.filter((_, i) => i !== qIdx);
    onUpdate({ ...section, questions });
  };

  return (
    <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden" data-testid={`section-${sectionIndex}`}>
      <div className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-[#1A1A1E] transition-colors duration-200" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <GripVertical className="w-4 h-4 text-[#A3A19C]" strokeWidth={1.5} />
          <div>
            <p className="text-sm font-semibold text-[#F5F2EB]">{pick(section, 'title')}</p>
            <p className="text-xs text-[#A3A19C]">{t('admin.questions_count', { n: (section.questions || []).length })}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-[#A3A19C] hover:text-red-400 transition-colors duration-300 p-1" data-testid={`delete-section-${sectionIndex}`}>
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-[#A3A19C]" /> : <ChevronDown className="w-4 h-4 text-[#A3A19C]" />}
        </div>
      </div>

      {expanded && (
        <div className="px-6 pb-6 border-t border-[rgba(255,255,255,0.06)]">
          <div className="grid grid-cols-2 gap-3 py-4">
            <InputField label={t('admin.sec_title_en')} value={section.title} onChange={v => onUpdate({ ...section, title: v })} testId={`section-title-${sectionIndex}`} />
            <InputField label={t('admin.sec_title_ar')} value={section.title_ar} onChange={v => onUpdate({ ...section, title_ar: v })} testId={`section-title-ar-${sectionIndex}`} />
          </div>

          <div className="space-y-3">
            {(section.questions || []).map((q, qIdx) => (
              <QuestionEditor
                key={q.id || qIdx}
                question={q}
                qIndex={qIdx}
                allQuestions={allQuestions}
                onUpdate={(updated) => updateQuestion(qIdx, updated)}
                onDelete={() => deleteQuestion(qIdx)}
              />
            ))}
          </div>

          <button onClick={addQuestion} className="mt-3 w-full py-2 border border-dashed border-[rgba(255,255,255,0.1)] rounded text-xs text-[#A3A19C] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300" data-testid={`add-question-${sectionIndex}`}>
            <Plus className="w-3 h-3 inline me-1" strokeWidth={1.5} /> {t('admin.add_question')}
          </button>
        </div>
      )}
    </div>
  );
};

// Question Editor
const QuestionEditor = ({ question, qIndex, allQuestions, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const { t, pick } = useLang();
  const q = question;
  const hasOptions = ['radio', 'checkbox'].includes(q.type);

  const addOption = () => {
    const options = [...(q.options || [])];
    options.push({ value: `option_${options.length}`, label: 'New Option', label_ar: 'خيار جديد' });
    onUpdate({ ...q, options });
  };

  const updateOption = (oIdx, field, value) => {
    const options = [...q.options];
    options[oIdx] = { ...options[oIdx], [field]: value };
    if (field === 'label') options[oIdx].value = value.toLowerCase().replace(/\s+/g, '_');
    onUpdate({ ...q, options });
  };

  const deleteOption = (oIdx) => {
    const options = q.options.filter((_, i) => i !== oIdx);
    onUpdate({ ...q, options });
  };

  return (
    <div className="bg-[#08080A] border border-[rgba(255,255,255,0.06)] rounded-lg">
      <div className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] text-[#D4AF37] font-mono bg-[#D4AF37]/10 px-1.5 py-0.5 rounded">{t(`qtype.${q.type}`)}</span>
          <p className="text-xs text-[#F5F2EB] truncate">{pick(q, 'text')}</p>
          {q.conditional_parent_id && <span className="text-[10px] text-[#E2B4BD] bg-[#E2B4BD]/10 px-1.5 py-0.5 rounded">{t('admin.conditional')}</span>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-[#A3A19C] hover:text-red-400 transition-colors duration-300 p-1">
            <Trash2 className="w-3 h-3" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-[rgba(255,255,255,0.04)] pt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <InputField label={t('admin.q_en')} value={q.text} onChange={v => onUpdate({ ...q, text: v })} testId={`q-text-${qIndex}`} />
            <InputField label={t('admin.q_ar')} value={q.text_ar} onChange={v => onUpdate({ ...q, text_ar: v })} testId={`q-text-ar-${qIndex}`} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-[#A3A19C] mb-1.5">{t('admin.type')}</label>
              <select value={q.type} onChange={e => {
                const newType = e.target.value;
                const update = { ...q, type: newType };
                if (['radio', 'checkbox'].includes(newType) && !q.options) update.options = [];
                if (newType === 'scale') { update.scale_min = 1; update.scale_max = 10; }
                onUpdate(update);
              }} data-testid={`q-type-${qIndex}`}
                className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37]">
                {QUESTION_TYPES.map(v => <option key={v} value={v}>{t(`qtype.${v}`)}</option>)}
              </select>
            </div>
            <InputField label={t('admin.hint_en')} value={q.hint || ''} onChange={v => onUpdate({ ...q, hint: v })} testId={`q-hint-${qIndex}`} />
            <InputField label={t('admin.hint_ar')} value={q.hint_ar || ''} onChange={v => onUpdate({ ...q, hint_ar: v })} testId={`q-hint-ar-${qIndex}`} />
          </div>

          {/* Conditional Logic */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#A3A19C] mb-1.5">{t('admin.show_if')}</label>
              <select value={q.conditional_parent_id || ''} onChange={e => onUpdate({ ...q, conditional_parent_id: e.target.value || null })}
                className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37]">
                <option value="">{t('admin.always')}</option>
                {allQuestions.filter(aq => aq.id !== q.id && aq.type === 'radio').map(aq => (
                  <option key={aq.id} value={aq.id}>{pick(aq, 'text')}</option>
                ))}
              </select>
            </div>
            {q.conditional_parent_id && (
              <div>
                <label className="block text-xs text-[#A3A19C] mb-1.5">{t('admin.when_equals')}</label>
                <input type="text" value={q.conditional_trigger_value || ''} onChange={e => onUpdate({ ...q, conditional_trigger_value: e.target.value })}
                  className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37]"
                  placeholder={t('admin.eg_yes')} />
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-[#A3A19C] cursor-pointer">
              <input type="checkbox" checked={q.required} onChange={e => onUpdate({ ...q, required: e.target.checked })} className="rounded" />
              {t('admin.required')}
            </label>
          </div>

          {/* Options editor */}
          {hasOptions && (
            <div>
              <label className="block text-xs text-[#A3A19C] mb-2">{t('admin.options')}</label>
              <div className="space-y-2">
                {(q.options || []).map((opt, oIdx) => (
                  <div key={oIdx} className="flex items-center gap-2">
                    <input type="text" value={opt.label} onChange={e => updateOption(oIdx, 'label', e.target.value)}
                      className="flex-1 bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded px-2 py-1.5 text-xs text-[#F5F2EB] focus:outline-none focus:border-[#D4AF37]" placeholder={t('admin.english')} />
                    <input type="text" value={opt.label_ar} onChange={e => updateOption(oIdx, 'label_ar', e.target.value)}
                      className="flex-1 bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded px-2 py-1.5 text-xs text-[#F5F2EB] focus:outline-none focus:border-[#D4AF37]" placeholder={t('admin.arabic')} dir="rtl" />
                    <button onClick={() => deleteOption(oIdx)} className="text-[#A3A19C] hover:text-red-400 p-1"><Trash2 className="w-3 h-3" strokeWidth={1.5} /></button>
                  </div>
                ))}
              </div>
              <button onClick={addOption} className="mt-2 text-xs text-[#D4AF37] hover:text-[#E5C07B]"><Plus className="w-3 h-3 inline" /> {t('admin.add_option')}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Assessment Response Card
const AssessmentResponseCard = ({ response, config, onSign, token }) => {
  const [expanded, setExpanded] = useState(false);
  const r = response;
  const answers = r.answers || {};
  const isSigned = r.coach_signed;
  const { t, pick, lang, fmtDateTime } = useLang();

  const getQuestionText = (qId) => {
    if (!config?.sections) return qId;
    for (const s of config.sections) {
      for (const q of (s.questions || [])) {
        if (q.id === qId) return pick(q, 'text');
      }
    }
    return qId;
  };

  const formatAnswer = (qId, val) => {
    if (!config?.sections) return String(val);
    for (const s of config.sections) {
      for (const q of (s.questions || [])) {
        if (q.id === qId && q.options) {
          if (Array.isArray(val)) {
            return val.map(v => {
              const opt = q.options.find(o => o.value === v);
              return opt ? pick(opt, 'label') : v;
            }).join(', ');
          }
          const opt = q.options.find(o => o.value === val);
          return opt ? pick(opt, 'label') : String(val);
        }
      }
    }
    return Array.isArray(val) ? val.join(', ') : String(val);
  };

  return (
    <div className={`bg-[#121215] border rounded-lg overflow-hidden ${isSigned ? 'border-green-500/20' : 'border-[rgba(255,255,255,0.08)]'}`}>
      <div className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-[#1A1A1E] transition-colors duration-200" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-[#F5F2EB]">{answers.full_name || t('common.anonymous')}</p>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isSigned ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                {isSigned ? t('dash.signed') : t('dash.pending')}
              </span>
            </div>
            <p className="text-xs text-[#A3A19C]"><span className="ltr-iso">{answers.phone || '--'}</span> · {fmtDateTime(r.submitted_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isSigned && (
            <button onClick={(e) => { e.stopPropagation(); onSign(r.id); }} className="text-xs bg-[#D4AF37] text-[#08080A] px-3 py-1.5 rounded-full font-semibold hover:bg-[#E5C07B] transition-colors duration-300" data-testid={`sign-${r.id}`}>
              {t('admin.sign')}
            </button>
          )}
          <a href={`${API}/admin/assessment/${r.id}/pdf?token=${token}&lang=${lang}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[#D4AF37] hover:text-[#E5C07B] p-1" data-testid={`pdf-${r.id}`}>
            <Download className="w-4 h-4" strokeWidth={1.5} />
          </a>
          {expanded ? <ChevronUp className="w-4 h-4 text-[#A3A19C]" /> : <ChevronDown className="w-4 h-4 text-[#A3A19C]" />}
        </div>
      </div>
      {expanded && (
        <div className="px-6 pb-6 border-t border-[rgba(255,255,255,0.06)] pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(answers).filter(([k]) => !['acknowledgment', 'terms_accepted'].includes(k)).map(([key, val]) => (
              <div key={key} className="bg-[#08080A] rounded-lg px-4 py-3">
                <p className="text-[10px] text-[#A3A19C] uppercase tracking-wider mb-1">{getQuestionText(key)}</p>
                <p className="text-xs text-[#F5F2EB]">{formatAnswer(key, val)}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-4">
            {answers.terms_accepted && (
              <div className="flex items-center gap-2 text-xs text-green-400">
                <CheckCircle className="w-3 h-3" strokeWidth={2} /> {t('admin.terms_ok')}
              </div>
            )}
            {answers.acknowledgment && (
              <div className="flex items-center gap-2 text-xs text-green-400">
                <CheckCircle className="w-3 h-3" strokeWidth={2} /> {t('admin.ack_ok')}
              </div>
            )}
          </div>
          {isSigned && (
            <div className="mt-3 bg-green-500/5 border border-green-500/20 rounded-lg px-4 py-3">
              <p className="text-xs text-green-400 font-semibold">{t('admin.signed_by_on', { name: r.coach_signer_name, date: fmtDateTime(r.coach_signed_at) })}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Admin BIA Input
const AdminBIAInput = ({ label, field, form, setForm }) => (
  <div>
    <label className="block text-[10px] text-[#A3A19C] mb-1">{label}</label>
    <input type="number" step="0.1" value={form[field] || ''} onChange={e => setForm({ ...form, [field]: e.target.value })}
      data-testid={`admin-bia-${field}`}
      className="w-full bg-[#08080A] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
      placeholder="--" />
  </div>
);

export default AdminPage;
